import {
  PRODUCT_CODE_TARGET,
  PRODUCT_NAME_TARGET,
  PROJECT_CODE_TARGET,
  PROJECT_NAME_TARGET,
  type ImportMapping,
  type ImportTransform,
} from './transform';

export interface RecommendedMappingTarget {
  field: string;
  label: string;
}

export const RECOMMENDED_IMPORT_MAPPING_TARGETS: RecommendedMappingTarget[] = [
  { field: 'work_order_name', label: 'Work Order' },
  { field: 'product_type', label: 'Product Type' },
  { field: PROJECT_CODE_TARGET, label: 'Project Code' },
  { field: PROJECT_NAME_TARGET, label: 'Project' },
];

export function getMissingRecommendedMappingFields(mapping: ImportMapping): string[] {
  const byField = new Map(mapping.map((entry) => [entry.targetField, entry.transform] as const));
  // A mapped Product / Product Code supplies the Product Type (and every other
  // container option) from the resolved product, so flagging Product Type as
  // missing would be wrong — the editor disables that row entirely.
  const productMapped =
    isUsableTransform(byField.get(PRODUCT_NAME_TARGET)) ||
    isUsableTransform(byField.get(PRODUCT_CODE_TARGET));
  return RECOMMENDED_IMPORT_MAPPING_TARGETS.filter((target) => {
    if (productMapped && target.field === 'product_type') return false;
    return !isUsableTransform(byField.get(target.field));
  }).map((target) => target.label);
}

/**
 * Fields an import genuinely cannot run without, as display labels — empty when
 * the mapping is good to go. Distinct from the *recommended* list above, which
 * only warns.
 *
 * Work Order is the match key, so it is always required. Project Code is
 * required only when the import has no project pinned to fall back on (launched
 * from the Projects index, or an Automatic Drive import): without it every row
 * errors with "no project column is mapped and the import has no project".
 */
export function getMissingRequiredMappingFields(
  mapping: ImportMapping,
  opts: { hasProject: boolean },
): string[] {
  const byField = new Map(mapping.map((entry) => [entry.targetField, entry.transform] as const));
  const missing: string[] = [];
  if (!isUsableTransform(byField.get('work_order_name'))) missing.push('Work Order');
  if (!opts.hasProject && !isUsableTransform(byField.get(PROJECT_CODE_TARGET))) {
    missing.push('Project Code');
  }
  return missing;
}

/**
 * Whether a transform would actually yield a value — a mapped-but-empty row
 * (blank static, single-column concat, headerless column pick) is as good as
 * unmapped. Shared with the import dialog's required-field gate.
 */
export function isUsableTransform(transform: ImportTransform | undefined): boolean {
  if (!transform) return false;
  switch (transform.kind) {
    case 'static':
      return transform.value.trim() !== '';
    case 'concat':
      return transform.headers.length >= 2;
    case 'whole':
    case 'first_n':
    case 'last_n':
    case 'after_n':
      return transform.header.trim() !== '';
  }
}
